/**
 * Crease morph graft — ADR-028 E3's creation half: writes one named morph
 * target onto a skinned mesh from rest-space per-vertex deltas.
 *
 * Nothing in this repo has ever CREATED a morph target before this door; the
 * evaluator's `morphWeights` channel (E3's declaration, D5) has been waiting
 * for a target to drive. This is the other end of that channel, and it is
 * deliberately dumb: it takes deltas and stores them. WHERE the deltas come
 * from is D2's derivation (delta-mush-vs-LBS diff at sample poses), which is
 * the caller's problem and a separate build — injected here rather than
 * computed, so this door is provable on its own.
 *
 * Rest-space, not posed space: a morph target is a displacement of the BIND
 * mesh, applied before skinning. The same law as every other door here (the
 * elbow-evidence rule — a playing clip must not move the measurement).
 *
 * Deltas arrive SPARSE (a crease moves ~1–3% of a body's vertices) and are
 * stored DENSE, because three's morphAttributes are dense buffers. That is a
 * runtime storage fact, not a payload decision: whether the EXPORT stays
 * sparse is ADR-028 D5's open Q1, and the answer does not change this door.
 *
 * Position deltas only FOR A CREASE. Normal deltas are D5's open Q3 for
 * creases (measure before declaring), and the crease entrance refuses to guess.
 * The SURFACE entrance ({@link graftSurfaceMorph}, ADR-038 §14, J4's jiggle
 * modes) carries a normal delta per vertex: a membrane mode's read IS its
 * moving highlight, so there the normal channel is the point. One door, two
 * entrances, the same adoption and channel-alignment laws.
 *
 * Depends on: three. Depended on by: the Document driver's hinge pass, after
 * the shape derivation.
 */
import { Float32BufferAttribute, type SkinnedMesh } from 'three';

/** One vertex's rest-space displacement. Sparse by construction. */
export interface CreaseDelta {
  readonly vertex: number;
  readonly dx: number;
  readonly dy: number;
  readonly dz: number;
}

/** A surface mode's per-vertex delta: the displacement AND the normal's turn (ADR-038 §14.2.6). */
export interface SurfaceDelta extends CreaseDelta {
  readonly nx: number;
  readonly ny: number;
  readonly nz: number;
}

export interface CreaseMorphReport {
  /** The morph target's name — the key the evaluator's ramp drives. */
  readonly name: string;
  /** Its index in `morphTargetInfluences`. glTF addresses influences BY INDEX (D5 Q6). */
  readonly index: number;
  /**
   * TRUE when the mesh already carried this target and nothing was written —
   * a re-imported augmented export ADOPTS its correctives rather than grafting
   * them twice (#110's law). Bit-for-bit no-op, and the report says so.
   */
  readonly adopted: boolean;
  readonly verticesMoved: number;
  readonly largestDelta: number;
}

export class CreaseMorphError extends Error {
  constructor(readonly issues: readonly string[], what = 'crease morph') {
    super(`Cannot graft ${what} (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

/** Every corrective wears the class, morph targets included (ADR-028 §6e, D5). */
const CORRECTIVE_SUFFIX = '_corr';

/**
 * Graft one named crease morph onto `mesh`, or adopt it if already present.
 *
 * Refuses loudly rather than producing a target nobody can see: a name outside
 * the corrective class, an empty delta set (it names no shape), an
 * out-of-range or repeated vertex, a non-finite component, or a geometry
 * already carrying ABSOLUTE morph targets (mixing the two silently corrupts
 * every existing target — glTF morphs are relative).
 */
export function graftCreaseMorph(mesh: SkinnedMesh, name: string, deltas: readonly CreaseDelta[]): CreaseMorphReport {
  const issues: string[] = [];
  if (!name.endsWith(CORRECTIVE_SUFFIX)) {
    issues.push(`morph name "${name}" does not wear the corrective class "${CORRECTIVE_SUFFIX}" — re-import adopts by that class (#110)`);
  }
  return graftMorph(mesh, name, deltas, issues, 'crease morph');
}

/** The surface jiggle's class (ADR-038 §14.5): `surface.<Region>.<k>`, never `jiggle-*` (the bounce springs' names). */
export const SURFACE_MORPH_PREFIX = 'surface.';

/**
 * Graft (or adopt) one surface-jiggle mode: position AND normal deltas. The
 * normal channel is created when the geometry has none (earlier targets get
 * zero normal deltas — exact, they turn nothing), so the channels stay aligned
 * by index the way the crease door keeps them.
 */
export function graftSurfaceMorph(mesh: SkinnedMesh, name: string, deltas: readonly SurfaceDelta[]): CreaseMorphReport {
  const issues: string[] = [];
  if (!name.startsWith(SURFACE_MORPH_PREFIX)) {
    issues.push(`morph name "${name}" does not wear the surface class "${SURFACE_MORPH_PREFIX}" — re-import adopts by that class`);
  }
  for (const d of deltas) {
    if (!Number.isFinite(d.nx) || !Number.isFinite(d.ny) || !Number.isFinite(d.nz)) issues.push(`morph "${name}" has a non-finite normal delta at vertex ${d.vertex}`);
  }
  return graftMorph(mesh, name, deltas, issues, 'surface morph');
}

function graftMorph(mesh: SkinnedMesh, name: string, deltas: readonly (CreaseDelta | SurfaceDelta)[], issues: string[], what: string): CreaseMorphReport {
  const geometry = mesh.geometry;
  const position = geometry.getAttribute('position');
  if (position === undefined) throw new CreaseMorphError(['the mesh has no position attribute'], what);
  const count = position.count;

  const existing = geometry.morphAttributes['position'] ?? [];
  // Adoption must read BOTH names, and the second one is the load-bearing one.
  // three writes a morph target's name onto the BufferAttribute when WE build
  // it, but a re-imported GLB restores names only into the MESH's
  // morphTargetDictionary (from `extras.targetNames`) — the attributes come
  // back anonymous. Checking the attribute alone therefore never recognises a
  // re-imported crease, so every reopen appends ANOTHER full set of targets:
  // 4, then 8, then 12, growing the file and stacking corrections that were
  // already in the paint (Codex P2 on #113; the same #110 law the helper bones
  // already obey).
  const already =
    existing.findIndex((a) => a.name === name) >= 0
      ? existing.findIndex((a) => a.name === name)
      : (mesh.morphTargetDictionary?.[name] ?? -1);
  if (already >= 0) {
    // Adoption: the target is already here (a re-imported export, or a SIBLING
    // MESH that shares this geometry). Touch no DATA — the law is bit-for-bit,
    // so we do not even rewrite equal values.
    //
    // But per-mesh state is not data. `morphTargetDictionary` and
    // `morphTargetInfluences` live on the MESH, while the attribute lives on
    // the geometry — so when two SkinnedMeshes share one BufferGeometry,
    // grafting the first left every sibling's dictionary stale, the adoption
    // check saw the attribute and skipped them, and the mount could not find
    // the crease to drive on any mesh but the first (Codex P2, #113).
    if (mesh.morphTargetDictionary?.[name] === undefined) mesh.updateMorphTargets();
    if (issues.length > 0) throw new CreaseMorphError(issues, what);
    return { name, index: already, adopted: true, verticesMoved: 0, largestDelta: 0 };
  }

  if (existing.length > 0 && geometry.morphTargetsRelative !== true) {
    issues.push('geometry carries ABSOLUTE morph targets; adding a relative one would corrupt them');
  }
  if (deltas.length === 0) {
    issues.push(`morph "${name}" has no deltas — it names no shape`);
  }

  const seen = new Set<number>();
  let largestDelta = 0;
  for (const d of deltas) {
    if (!Number.isInteger(d.vertex) || d.vertex < 0 || d.vertex >= count) {
      issues.push(`morph "${name}" displaces vertex ${d.vertex}, outside the mesh's 0..${count - 1}`);
      continue;
    }
    if (seen.has(d.vertex)) {
      issues.push(`morph "${name}" displaces vertex ${d.vertex} twice — the second would silently win`);
      continue;
    }
    seen.add(d.vertex);
    if (!Number.isFinite(d.dx) || !Number.isFinite(d.dy) || !Number.isFinite(d.dz)) {
      issues.push(`morph "${name}" has a non-finite delta at vertex ${d.vertex}`);
      continue;
    }
    largestDelta = Math.max(largestDelta, Math.hypot(d.dx, d.dy, d.dz));
  }
  if (issues.length > 0) throw new CreaseMorphError(issues, what);

  // Sparse in, dense out: three's morphAttributes are dense buffers. Untouched
  // vertices hold an exact zero, so they are bit-identical under any influence.
  const dense = new Float32Array(count * 3);
  for (const d of deltas) {
    dense[d.vertex * 3] = d.dx;
    dense[d.vertex * 3 + 1] = d.dy;
    dense[d.vertex * 3 + 2] = d.dz;
  }
  // EVERY channel gets an entry, not just position (Codex P2, #113).
  // three's updateMorphTargets() builds the influence array and the dictionary
  // from whichever channel key comes FIRST in morphAttributes — so a geometry
  // that already carries a normal- or tangent-only morph would leave our name
  // out of the dictionary entirely and the viewport would never drive it. The
  // companions are zero-filled: a crease displaces positions, and adding a
  // zero normal delta changes nothing while keeping the channels aligned.
  const attribute = new Float32BufferAttribute(dense, 3);
  // three names targets from the ATTRIBUTE's name (Mesh.updateMorphTargets),
  // and GLTFExporter writes those into `extras.targetNames` — which is the
  // channel D5 Q6's binder resolves a name through. The name is load-bearing.
  attribute.name = name;
  // EVERY channel ends the same length, and position is PADDED UP first.
  //
  // The previous attempt appended to each channel independently, which is
  // wrong when the channels start uneven: a geometry with N normal-only morphs
  // and no position morphs got a one-entry position channel against N+1
  // normals, so updateMorphTargets() (which reads the FIRST channel) gave the
  // crease index N while its displacement sat at index 0 — the declared crease
  // moved nothing and an unrelated morph drove it instead (Codex P2, #113).
  const channels = ['normal', 'color'] as const;
  const slots = Math.max(existing.length, ...channels.map((c) => geometry.morphAttributes[c]?.length ?? 0));
  // A surface mode brings its own normal deltas (a crease brings none).
  const withNormals = deltas.length > 0 && 'nx' in deltas[0]!;
  let normals: Float32Array | null = null;
  if (withNormals) {
    normals = new Float32Array(count * 3);
    for (const d of deltas as readonly SurfaceDelta[]) {
      normals[d.vertex * 3] = d.nx;
      normals[d.vertex * 3 + 1] = d.ny;
      normals[d.vertex * 3 + 2] = d.nz;
    }
  }
  const pad = (list: readonly (typeof attribute)[], itemSize: number, upTo: number): (typeof attribute)[] => {
    const out = [...list];
    while (out.length < upTo) {
      const filler = new Float32BufferAttribute(new Float32Array(count * itemSize), itemSize);
      filler.name = `_pad${out.length}`;
      out.push(filler);
    }
    return out;
  };
  geometry.morphAttributes['position'] = [...pad(existing as never, 3, slots), attribute];
  for (const channel of channels) {
    const others = geometry.morphAttributes[channel];
    if (channel === 'normal' && normals !== null) {
      // Our normal deltas; a geometry with no normal channel yet gets one, every earlier slot zero.
      const own = new Float32BufferAttribute(normals, 3);
      own.name = name;
      geometry.morphAttributes[channel] = [...pad((others ?? []) as never, 3, slots), own];
      continue;
    }
    if (others === undefined || others.length === 0) continue;
    const itemSize = others[0]!.itemSize;
    const filler = new Float32BufferAttribute(new Float32Array(count * itemSize), itemSize);
    filler.name = name;
    geometry.morphAttributes[channel] = [...pad(others as never, itemSize, slots), filler];
  }
  geometry.morphTargetsRelative = true;
  // NAME THE EXISTING TARGETS BEFORE REBUILDING, or rebuilding renames them.
  //
  // updateMorphTargets() builds the dictionary from ATTRIBUTE names — and an
  // imported GLB's morph attributes are anonymous, their names living only in
  // the mesh's dictionary (the same asymmetry the adoption check above exists
  // for). Rebuilding without this would replace an imported `Smile` with an
  // index string, destroying names a person authored and that any re-export
  // would then ship wrong (Codex P1, #113). We add a crease; we do not get to
  // cost someone their blendshape names.
  const existingNames = new Map(Object.entries(mesh.morphTargetDictionary ?? {}).map(([n, i]) => [i, n]));
  for (const channel of ['position', 'normal', 'color'] as const) {
    const list = geometry.morphAttributes[channel];
    if (list === undefined) continue;
    list.forEach((attr, i) => {
      const authored = existingNames.get(i);
      if (attr.name === '' && authored !== undefined) attr.name = authored;
    });
  }
  mesh.updateMorphTargets();

  const index = geometry.morphAttributes['position']!.length - 1;
  return { name, index, adopted: false, verticesMoved: deltas.length, largestDelta };
}
